// Open Haunt's calendar (zones/open-haunt/zone.json "moments", mirrored here until zone specs are served; a test keeps
// the two equal) and the countdowns the hub, the Home portal and the Events cards show. Pure: `now` is always passed.

export const SEASON_START = Date.parse("2026-10-23T00:00:00Z");
export const SHIP_BY = Date.parse("2026-11-06T23:59:59Z");
export const NIGHT_AT = Date.parse("2026-11-07T20:00:00Z");
export const NIGHT_UNTIL = Date.parse("2026-11-08T00:00:00Z");
export const SEASON_END = Date.parse("2026-11-10T23:59:59Z");
/** The Home portal shows from Tuesday, ahead of the season. */
export const PORTAL_FROM = Date.parse("2026-10-20T00:00:00Z");

export interface Moment {
  id: "season-start" | "ship-by" | "plaza-night" | "row-ends";
  at: number;
  until?: number;
  label: string;
  /** short date for the timeline, in UTC so it reads the same everywhere */
  day: string;
}

export const MOMENTS: Moment[] = [
  { id: "season-start", at: SEASON_START, label: "Open Haunt starts", day: "Fri 23 Oct" },
  { id: "ship-by", at: SHIP_BY, label: "Last day to ship a haunt", day: "Fri 6 Nov" },
  { id: "plaza-night", at: NIGHT_AT, until: NIGHT_UNTIL, label: "Haunt night in the Plaza", day: "Sat 7 Nov" },
  { id: "row-ends", at: SEASON_END, label: "The Halloween row ends", day: "Tue 10 Nov" },
];

export type Phase = "soon" | "live" | "night" | "after-night" | "over";

export function phaseAt(now: number): Phase {
  if (now < SEASON_START) return "soon";
  if (now < NIGHT_AT) return "live";
  if (now < NIGHT_UNTIL) return "night";
  if (now <= SEASON_END) return "after-night";
  return "over";
}

/** Whole days, hours, minutes and seconds left until `target` (all zero once it has passed). */
export function remaining(target: number, now: number): { d: number; h: number; m: number; s: number; done: boolean } {
  const ms = Math.max(0, target - now);
  const t = Math.floor(ms / 1000);
  return { d: Math.floor(t / 86400), h: Math.floor((t % 86400) / 3600), m: Math.floor((t % 3600) / 60), s: t % 60, done: ms === 0 };
}

/** "4d 06h 12m", "06h 12m 09s" under a day. */
export function countdownText(target: number, now: number): string {
  const r = remaining(target, now);
  const p = (n: number) => String(n).padStart(2, "0");
  return r.d > 0 ? `${r.d}d ${p(r.h)}h ${p(r.m)}m` : `${p(r.h)}h ${p(r.m)}m ${p(r.s)}s`;
}

/** What the hero's clock says now: a status line, and what it counts down to (null when nothing is left). */
export function heroClock(now: number): { status: string; label: string | null; target: number | null; live: boolean } {
  switch (phaseAt(now)) {
    case "soon":
      return { status: "Starts Friday 23 October", label: "Opens in", target: SEASON_START, live: false };
    case "live":
      return { status: "Live now", label: "Haunt night in", target: NIGHT_AT, live: true };
    case "night":
      return { status: "Live now in the Plaza", label: "Haunt night ends in", target: NIGHT_UNTIL, live: true };
    case "after-night":
      return { status: "Live now", label: "The Halloween row ends in", target: SEASON_END, live: true };
    default:
      return { status: "Open Haunt is over. See you next year.", label: null, target: null, live: false };
  }
}

/** The Plaza night's card: "In 12d 20h 05m", "Live now", or over. */
export function nightClock(now: number, at = NIGHT_AT, until = NIGHT_UNTIL): { state: "soon" | "live" | "over"; text: string } {
  if (now < at) return { state: "soon", text: `Starts in ${countdownText(at, now)}` };
  if (now < until) return { state: "live", text: "Live now in the Plaza" };
  return { state: "over", text: "Thanks for coming" };
}

export type MomentState = "past" | "now" | "next" | "later";

/** Each moment on the timeline: done, happening, the next one, or later. */
export function timeline(now: number): { m: Moment; state: MomentState }[] {
  let nextGiven = false;
  return MOMENTS.map((m) => {
    const end = m.until ?? m.at;
    if (m.until && now >= m.at && now < m.until) return { m, state: "now" as const };
    if (now >= end) {
      // the season's start stays "now" while the season runs and the night hasn't started
      return { m, state: m.id === "season-start" && phaseAt(now) === "live" ? ("now" as const) : ("past" as const) };
    }
    if (!nextGiven) {
      nextGiven = true;
      return { m, state: "next" as const };
    }
    return { m, state: "later" as const };
  });
}

/** Whether the Home portal shows (from Tuesday 20 Oct to the row's end). */
export function portalOn(now: number): boolean {
  return now >= PORTAL_FROM && now <= SEASON_END;
}

/**
 * The page's clock. Dev only: `?hauntNow=2026-11-07T20:30Z` on a dev server shows another day, for the screenshot checks
 * of each phase. Production always uses the real time.
 */
export function zoneNow(loc: Pick<Location, "hostname" | "search"> = location): number {
  // a dev server: a dotless host name or a bare IP (pages/developers.ts showHeld), never banger.sh
  const local = /^[^.]+$|^(\d+\.){3}\d+$/.test(loc.hostname);
  const at = local ? new URLSearchParams(loc.search).get("hauntNow") : null;
  const t = at ? Date.parse(at) : NaN;
  return Number.isFinite(t) ? t : Date.now();
}
